import { useCallback } from "react";
import { chunk } from "@common/helpers/collections";
import { BULK_UNMATCH_INVOICES_TX_CAP } from "@common/schemas/domains/accrualReview/accrual-review.schema";
import { bulkUnmatchInvoices } from "@api/accrual-review.api";
import { invalidateInvoiceQueries } from "@api/invoice.api";
import { useScope } from "@state/scopeContext.lib";
import { useToaster } from "@design-system/ToastMessages/useToaster";
import { invalidateTransactionsQueries } from "../useTransactionsQuery";
import { hasUnmatchableInvoiceMatch } from "../useTransactionsBulkActionConditions.helpers";
import { useBulkActions } from "./useBulkActions";
import {
    BulkUnmatchInvoicesCounts,
    bulkUnmatchInvoicesMessage,
} from "./bulkUnmatchInvoicesToast";

/**
 * Bulk "Unmatch invoices": sends the selected transactions that carry an
 * invoice match or a suggested invoice match in batches of the server cap
 * (the server also skips any without one, without counting them as failures), then refreshes the
 * transactions queries and clears the selection. A batch whose request fails
 * counts all of its transactions as failed and the remaining batches still
 * run.
 */
export function useBulkUnmatchInvoices() {
    const bulkActions = useBulkActions();
    const { workspaceId } = useScope();
    const { toast, error: toastError } = useToaster();

    return useCallback(async () => {
        const transactionIds = bulkActions.selectedArray
            .filter(hasUnmatchableInvoiceMatch)
            .map((transaction) => transaction.id);
        if (!workspaceId || transactionIds.length === 0) {
            return;
        }

        const totals: BulkUnmatchInvoicesCounts = {
            unmatchedCount: 0,
            failedCount: 0,
            transferMatchSkippedCount: 0,
        };
        for (const batch of chunk(
            transactionIds,
            BULK_UNMATCH_INVOICES_TX_CAP,
        )) {
            try {
                const response = await bulkUnmatchInvoices({
                    workspaceId,
                    transactionIds: batch,
                });
                totals.unmatchedCount += response.unmatchedCount;
                totals.failedCount += response.failedCount;
                totals.transferMatchSkippedCount +=
                    response.transferMatchSkippedCount;
            } catch {
                totals.failedCount += batch.length;
            }
        }

        const message = bulkUnmatchInvoicesMessage(totals);
        if (message !== null) {
            toast(message);
        } else if (totals.failedCount > 0) {
            toastError("Failed to unmatch invoices");
        } else {
            toast("No invoice matches to unmatch");
        }
        if (totals.unmatchedCount > 0) {
            void invalidateInvoiceQueries();
        }
        await invalidateTransactionsQueries();
        // Keep the selection when every attempt failed so it can be retried.
        if (totals.unmatchedCount > 0 || totals.failedCount === 0) {
            bulkActions.clearSelection();
        }
    }, [bulkActions, workspaceId, toast, toastError]);
}
